import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { eramanCMSApi, post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { fields } from "../constants";
import { useLevelDocumentDetails } from "./Level.hook";

export const AddEditStoreLevel = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editLevel,
    isLoading: drilldownLoading,
    refetch: refetchCategories,
  } = useLevelDocumentDetails(query.get("editId") || "");

  const {
    data: editLevelById,
    isLoading: isLoadingProducts,
    refetch: refetchOutletDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [airportOptions, setAirportOptions] = useState<any[]>([]);
  const [storeOptions, setStoreOptions] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);

  // Fetch dropdowns
  const fetchDropdowns = async () => {
    try {
      const [airportsRes, storesRes] = await Promise.all([
        eramanCMSApi.get(`/api/airport-dropdown`),
        eramanCMSApi.get(`/api/get-outlet-dropdown`),
      ]);

      setAirportOptions(
        airportsRes.data?.map((item: any) => ({
          label: decodeHTML(item.name),
          value: item.id,
        })) || []
      );

      setStoreOptions(
        storesRes.data?.map((item: any) => {
          const parts = item.field_stores_name.split(",");
          const formattedLabel =
            parts.length > 1
              ? parts[0] + ", " + parts.slice(1).join(",") // ensures space after first comma
              : item.field_stores_name;

          return {
            label: decodeHTML(formattedLabel),
            value: item.id,
          };
        }) || []
      );
    } catch (error) {
      console.error("Dropdown fetch failed", error);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    if (query.get("id")) refetchOutletDocument();
    if (query.get("editId")) refetchCategories();
  }, [query.get("id"), query.get("editId")]);

  const getRecords = () => {
    let url = `/api/get-location-map-levels-data`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data && res.data.data) {
          let envelopes = res.data.data;
          setRecords(envelopes);
        }
      })
      .catch(() => {
        setRecords([]);
      });
  };

  useEffect(() => {
    getRecords();
  }, []);

  const transformSelectValues = (data: FieldValues) => ({
    ...data,
    field_location_airports: data.field_location_airports
      ? [airportOptions.find((a) => a.value === data.field_location_airports)?.label || data.field_location_airports]
      : [],
    // field_stores_of_levels: Array.isArray(data.field_stores_of_levels)
    //   ? data.field_stores_of_levels.map((v: any) => Number(v))
    //   : data.field_stores_of_levels
    //     ? [Number(data.field_stores_of_levels)]
    //     : [],
    field_stores_of_levels: Array.isArray(data.field_stores_of_levels)
      ? data.field_stores_of_levels
          .map((v: any) => {
            const match = storeOptions.find((s) => s.value === v);
            return match ? { id: match.value, label: match.label } : null;
          })
          .filter(Boolean)
      : data.field_stores_of_levels
        ? [
            {
              id: Number(data.field_stores_of_levels),
              label: storeOptions.find((s) => s.value === Number(data.field_stores_of_levels))?.label,
            },
          ]
        : [],
  });

  // Compute initial values inline
  const formConfig: FormConfig = {
    initialValues: {
      field_location_level_name: editLevelById?.payload
        ? decodeHTML(editLevelById.payload.field_location_level_name)
        : decodeHTML(editLevel?.field_location_level_name || ""),
      field_location_level_name_ms: editLevelById?.payload
        ? decodeHTML(editLevelById.payload.field_location_level_name_ms)
        : decodeHTML(editLevel?.field_location_level_name_ms || ""),
      field_location_level_name_zh: editLevelById?.payload
        ? decodeHTML(editLevelById.payload.field_location_level_name_zh)
        : decodeHTML(editLevel?.field_location_level_name_zh || ""),
      field_store_location_code: editLevelById?.payload
        ? decodeHTML(editLevelById.payload.field_store_location_code)
        : decodeHTML(editLevel?.field_store_location_code || ""),

      field_location_airports: (() => {
        const airportVal =
          editLevelById?.payload?.field_location_airports ?? editLevel?.field_location_airports ?? null;
        if (!airportVal) return null;

        const match = airportOptions.find(
          (a) => a.label.trim().toLowerCase() === decodeHTML(String(airportVal)).trim().toLowerCase()
        );

        return match?.value || null;
      })(),

      field_stores_of_levels: (() => {
        const values = editLevelById?.payload?.field_stores_of_levels ?? editLevel?.field_stores_of_levels ?? [];

        // Normalize to array
        const arr = Array.isArray(values) ? values : [values];

        return arr
          .map((storeName: any) => {
            let normalized =
              typeof storeName === "string"
                ? storeName
                : typeof storeName === "number"
                  ? String(storeName)
                  : (storeName?.label ?? "");

            // Ensure space after commas if missing
            normalized = normalized.replace(/,([^ ])/g, ", $1");

            const match = storeOptions.find((s) => s.label.toLowerCase() === normalized.trim().toLowerCase());

            return match?.value; // return ID
          })
          .filter(Boolean);
      })(),

      field_location_level_image: editLevelById?.payload
        ? [
            {
              uid: "-1",
              name: "field_location_level_image.png",
              status: "done",
              url: editLevelById?.payload?.field_location_level_image,
            },
          ]
        : editLevel?.field_location_level_image
          ? [
              {
                uid: "-1",
                name: "field_location_level_image.png",
                status: "done",
                url: editLevel?.field_location_level_image,
              },
            ]
          : "",

      published: editLevelById?.payload ? editLevelById.payload.published : editLevel?.published || false,
      field_ranking: editLevelById?.payload ? editLevelById.payload.field_ranking : editLevel?.field_ranking || "",
    },
    fields: fields.level.map((field: any) => {
      if (field.name === "field_location_airports")
        return {
          ...field,
          options: airportOptions,
        };
      if (field.name === "field_stores_of_levels")
        return {
          ...field,
          options: storeOptions,
        };
      // if (field.name === "field_ranking") {
      //   return {
      //     ...field,
      //     validation: {
      //       required: `${field.label || field.name} is required`,
      //       validate: (value: string, formValues: FieldValues) => {
      //         if (!value) return true;
      //         const existingRecord = records.find((record: any) => {
      //           return (
      //             toInteger(record[field.name]) === toInteger(value) &&
      //             record.id !== (editLevel?.id || editLevelById?.payload?.id)
      //           );
      //         });
      //         return !existingRecord || `${field.label || field.name} already exists`;
      //       },
      //     },
      //   };
      // }
      return field;
    }),
  };

  // Submit handler
  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    const imageKeys = ["field_location_level_image"];
    for (const key of imageKeys) {
      try {
        const field = data[key];
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action,
      method,
      module: "Store Location",
      subModule: "level",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes,
      appList: [{ appName: "ERAMAN", apiUrl }],
      payload: {
        ...(action === "UPDATE" && { id: editLevel?.id || editLevelById?.payload?.id }),
        ...transformSelectValues(data),
        field_ranking: Number(data.field_ranking),
      },
      status,
    };

    console.log(payload, "payload");

    try {
      if (query.get("id")) {
        await put(`/management/update-document?approvalId=${query.get("id")}`, payload);
      } else {
        await post(`/management/publish`, payload);
      }
      openToast({
        content: status === "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
        type: "success",
      });
      navigate(`/dedicated/status-information?activeTab=${status === "PENDING" ? "pending" : "draft"}`);
    } catch (error) {
      openToast({ content: "Failed to save data", type: "error" });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Level`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingProducts}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            query.get("editId")
              ? `${eramanHost}/api/get-location-map-level-crud-data/${editLevel?.id || editLevelById?.payload?.id}`
              : `${eramanHost}/api/get-location-map-level-crud-data`,
            query.get("editId") ? "UPDATE" : "CREATE",
            query.get("editId") ? "PATCH" : "POST",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
      />
    </ContentWrapper>
  );
};
